import React from 'react'
import { Form } from 'react-bootstrap'
import { FolderOpen, Plus } from 'lucide-react'
import styled from 'styled-components'
import { Project } from '../../../types/models/project'

const ModeSelector = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const ModeButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.75rem 1rem;
  background: ${(props) =>
    props.$active
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'rgba(102, 126, 234, 0.1)'};
  border: 1px solid
    ${(props) => (props.$active ? 'rgba(102, 126, 234, 0.5)' : 'rgba(102, 126, 234, 0.2)')};
  border-radius: 8px;
  color: #fff;
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  font-size: 0.875rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;

  &:hover {
    background: ${(props) =>
      props.$active
        ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
        : 'rgba(102, 126, 234, 0.2)'};
    border-color: rgba(102, 126, 234, 0.5);
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 1.5rem;

  .form-label {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  .form-select,
  .form-control {
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(102, 126, 234, 0.3);
    border-radius: 8px;
    color: #fff;
    padding: 0.75rem 1rem;
    transition: all 0.3s ease;

    &:focus {
      background: rgba(0, 0, 0, 0.4);
      border-color: rgba(102, 126, 234, 0.6);
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      outline: none;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }

  .form-select {
    option {
      background: #1e293b;
      color: #fff;
    }
  }
`

interface TargetProjectSelectorProps {
  mode: 'existing' | 'new'
  setMode: (m: 'existing' | 'new') => void
  classificationProjects: Project[]
  targetProjectId: string
  setTargetProjectId: (id: string) => void
  newProjectName: string
  setNewProjectName: (name: string) => void
}

const TargetProjectSelector: React.FC<TargetProjectSelectorProps> = ({
  mode,
  setMode,
  classificationProjects,
  targetProjectId,
  setTargetProjectId,
  newProjectName,
  setNewProjectName,
}) => {
  return (
    <>
      <ModeSelector>
        <ModeButton $active={mode === 'existing'} onClick={() => setMode('existing')}>
          <FolderOpen size={18} />
          Existing
        </ModeButton>
        <ModeButton $active={mode === 'new'} onClick={() => setMode('new')}>
          <Plus size={18} />
          Create New
        </ModeButton>
      </ModeSelector>

      {mode === 'existing' ? (
        <StyledFormGroup>
          <Form.Label>Select Classification Project</Form.Label>
          <Form.Select value={targetProjectId} onChange={(e) => setTargetProjectId(e.target.value)}>
            <option value="">Choose a project...</option>
            {classificationProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
            {classificationProjects.length === 0 && (
              <option value="">No classification projects available</option>
            )}
          </Form.Select>
        </StyledFormGroup>
      ) : (
        <StyledFormGroup>
          <Form.Label>New Project Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter a name for the new project"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
          />
        </StyledFormGroup>
      )}
    </>
  )
}

export default TargetProjectSelector
